import { useMemo } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuthWrapper } from '@weareopenr/authkit';
import {
  ChannelIcon,
  HomeIcon,
  InsightsIcon,
  OrderIcon,
  PricingIcon,
  SpeechBubble,
  Spinner,
  VenueIcon,
  ViewIcon,
} from '@weareopenr/component-library';
import classNames from 'classnames';
import { EnvironmentSwitcher } from '../EnvironmentSwitcher/EnvironmentSwitcher';
import { useEnvironment, usePermissions } from '../../hooks';
import { isMockMode } from '../../constants';
import { mockUser } from '../../mocks/data';
import ProtectedRoute from '../ProtectedRoute';
import {
  Ask,
  Audit,
  Catalogue,
  Costs,
  Dashboard,
  Flags,
  InvoiceView,
  Invoices,
  NewTenant,
  Tenant,
  Tenants,
} from '../../pages';
import styles from './Layout.module.scss';

interface NavItem {
  label: string;
  path: string;
  Icon: typeof HomeIcon;
  visible: boolean;
}

const Layout = () => {
  const { pathname } = useLocation();
  const auth = useAuthWrapper();
  const user = isMockMode() ? mockUser : auth.user;
  const isLoading = isMockMode() ? false : auth.isLoading;
  const { signOut } = auth;
  const { canRead, canBill, canProvision } = usePermissions();
  const { isProd } = useEnvironment();

  const navItems: NavItem[] = useMemo(
    () => [
      { label: 'Overview', path: '/', Icon: HomeIcon, visible: true },
      { label: 'Tenants', path: '/tenants', Icon: VenueIcon, visible: canRead },
      { label: 'Catalogue', path: '/catalogue', Icon: PricingIcon, visible: canBill },
      { label: 'Invoices', path: '/invoices', Icon: OrderIcon, visible: canBill },
      { label: 'Costs', path: '/costs', Icon: InsightsIcon, visible: canRead },
      { label: 'Flags', path: '/flags', Icon: ChannelIcon, visible: canRead },
      { label: 'Audit', path: '/audit', Icon: ViewIcon, visible: canRead },
      { label: 'Ask', path: '/ask', Icon: SpeechBubble, visible: canRead },
    ],
    [canRead, canBill],
  );

  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner size="xlarge" colour="rainbow" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isActive = (path: string) => (path === '/' ? pathname === '/' : pathname.startsWith(path));
  const displayName =
    user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;
  const initials = (
    user.firstName && user.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`
      : user.email.slice(0, 2)
  ).toUpperCase();

  return (
    <div className={classNames(styles.shell, { [styles.prod]: isProd })}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <span className={styles.mark} aria-hidden="true">
            N
          </span>
          <span className={styles.brandText}>
            <span className={styles.brandName}>nexus</span>
            <span className={styles.brandSub}>openr control room</span>
          </span>
        </div>
        <p className={styles.navLabel}>Navigate</p>
        <nav className={styles.nav} aria-label="Main">
          {navItems
            .filter(({ visible }) => visible)
            .map(({ label, path, Icon }) => (
              <Link
                key={path}
                to={path}
                className={classNames(styles.navItem, { [styles.navActive]: isActive(path) })}
                aria-current={isActive(path) ? 'page' : undefined}
              >
                <Icon />
                <span>{label}</span>
              </Link>
            ))}
        </nav>
        <div className={styles.sidebarFooter}>
          <div className={styles.userRow}>
            <span className={styles.avatar} aria-hidden="true">
              {initials}
            </span>
            <div className={styles.userBlock}>
              <span className={styles.userName}>{displayName}</span>
              <button type="button" className={styles.signOut} onClick={() => signOut()}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </aside>
      <div className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <EnvironmentSwitcher />
            {isProd ? (
              <span className={styles.prodBadge} role="status">
                Production. Writes ask for confirmation.
              </span>
            ) : null}
          </div>
          <div className={styles.topbarRight}>
            <span className={styles.clock}>
              {new Date().toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
            </span>
          </div>
        </header>
        <main className={styles.content}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route
              path="/tenants"
              element={<ProtectedRoute element={<Tenants />} hasPermission={canRead} />}
            />
            <Route
              path="/tenants/new"
              element={<ProtectedRoute element={<NewTenant />} hasPermission={canProvision} />}
            />
            <Route
              path="/tenants/:tenantId/invoices/:invoiceId/view"
              element={<ProtectedRoute element={<InvoiceView />} hasPermission={canBill} />}
            />
            <Route
              path="/tenants/:tenantId/*"
              element={<ProtectedRoute element={<Tenant />} hasPermission={canRead} />}
            />
            <Route
              path="/invoices"
              element={<ProtectedRoute element={<Invoices />} hasPermission={canBill} />}
            />
            <Route
              path="/catalogue"
              element={<ProtectedRoute element={<Catalogue />} hasPermission={canBill} />}
            />
            <Route path="/billing/*" element={<Navigate to="/catalogue" replace />} />
            <Route
              path="/costs"
              element={<ProtectedRoute element={<Costs />} hasPermission={canRead} />}
            />
            <Route
              path="/flags"
              element={<ProtectedRoute element={<Flags />} hasPermission={canRead} />}
            />
            <Route
              path="/audit"
              element={<ProtectedRoute element={<Audit />} hasPermission={canRead} />}
            />
            <Route
              path="/ask"
              element={<ProtectedRoute element={<Ask />} hasPermission={canRead} />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Layout;
